import { Box, Typography } from '@mui/material';
import React from 'react';
import emptyContentImageMedium from '../assets/emptyContentImageMedium.svg';
import emptyContentImageSmall from '../assets/emptyContentImageSmall.svg';
import { DashedWrapper } from './components/DashedWrapper';
/**
 * Displays a dashed wrapper with an illustration and descriptive text when there is no content.
 * It also supports optional child elements for additional actions or content.
 *
 * @param {EmptyContentWrapperProps} root0 - Component props.
 * @param {'small' | 'medium'} [root0.imageSize] - Optional image size variant to control the illustration's size.
 * @param {string} [root0.altImageText] - Optional alt text for the illustration. Defaults to "No content".
 * @param {string} root0.description - Descriptive text explaining the absence of content.
 * @param {React.ReactNode} root0.children - Optional elements to display alongside the description (e.g. Buttons).
 *
 * @returns {JSX.Element} A rendered wrapper with an illustration, description, and optional content.
 */
export const EmptyContentWrapper = ({ imageSize, altImageText, description, children }) => {
    const isImageSmall = imageSize === 'small';
    return (React.createElement(DashedWrapper, null,
        React.createElement(Box, { sx: {
                py: { xs: 2, lg: 3 },
                px: { xs: 2, lg: 3 },
                display: 'flex',
                justifyContent: 'center',
                width: isImageSmall ? '50%' : 'unset',
            } },
            React.createElement(Box, { component: 'img', src: isImageSmall ? emptyContentImageSmall : emptyContentImageMedium, alt: altImageText !== null && altImageText !== void 0 ? altImageText : 'No content', sx: {
                    height: { xs: '100%', lg: 'unset' },
                    width: { xs: '100%', lg: 'unset' },
                } })),
        React.createElement(Box, { sx: {
                px: 2,
                pb: { xs: 4, lg: 'unset' },
                width: isImageSmall ? '50%' : 'unset',
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', lg: isImageSmall ? 'row' : undefined },
                alignItems: isImageSmall ? 'center' : undefined,
                justifyContent: 'space-between',
            } },
            React.createElement(Typography, { variant: 'subtitle1', sx: {
                    textAlign: { xs: 'center', lg: 'unset' },
                }, "data-test": 'description' }, description),
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    alignItems: 'center',
                } }, children))));
};
